import * as React from 'react';
import { useUser } from '@clerk/tanstack-react-start';
import { useQuery } from 'convex/react';
import { api } from '@/convex';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useProjectContext } from '@/hooks/useProjectContext';
import { useBindingFilter } from '@/hooks/useBindingFilter';
import type {
  DashboardFeedItem,
  DashboardMetrics,
  FeedStatus,
} from './dashboard-surface';

// ── Status mapping for audit actions ──────────────────────────

const ACTION_STATUS: Record<string, FeedStatus> = {
  'enrichment.updated': 'pass',
  'enrichment.deleted': 'warn',
  'component.activated': 'pass',
  'component.deactivated': 'warn',
  'check.ingested': 'pass',
  'finding.resolved': 'pass',
  'finding.ignored': 'warn',
  'finding.assigned': 'info',
  'finding.reopened': 'fail',
  'policy.created': 'info',
  'policy.updated': 'info',
  'policy.deleted': 'warn',
  'policy_exception.created': 'warn',
  'policy_exception.removed': 'info',
  'member.invited': 'info',
  'member.removed': 'warn',
  'member.role_changed': 'info',
  'api_key.created': 'info',
  'api_key.revoked': 'warn',
  'project.created': 'info',
  'project.deleted': 'fail',
  'project.ds_config_updated': 'info',
  'org.settings_changed': 'info',
  'binding.linked_ds': 'pass',
};

const ACTION_LABEL: Record<string, string> = {
  'enrichment.updated': 'Enrichment updated',
  'enrichment.deleted': 'Enrichment removed',
  'component.activated': 'Component activated',
  'component.deactivated': 'Component deactivated',
  'check.ingested': 'Governance check ingested',
  'finding.resolved': 'Finding resolved',
  'finding.ignored': 'Finding ignored',
  'finding.assigned': 'Finding assigned',
  'finding.reopened': 'Finding reopened',
  'policy.created': 'Policy created',
  'policy.updated': 'Policy updated',
  'policy.deleted': 'Policy deleted',
  'member.invited': 'Member invited',
  'member.removed': 'Member removed',
  'member.role_changed': 'Member role changed',
  'api_key.created': 'API key created',
  'api_key.revoked': 'API key revoked',
  'project.created': 'Project created',
  'project.deleted': 'Project deleted',
  'project.ds_config_updated': 'DS config updated',
  'org.settings_changed': 'Org settings changed',
  'binding.linked_ds': 'Design system linked',
};

// ── Time formatting ───────────────────────────────────────────

function formatTimeAgo(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

// ── Hook ──────────────────────────────────────────────────────

export function useDashboardData(): {
  metrics: DashboardMetrics | null;
  isLoading: boolean;
} {
  const { orgId } = useOrgContext();
  const { selectedProjectId, isLoading: projectLoading } = useProjectContext();
  const { selectedBindingId, isLoading: bindingLoading } = useBindingFilter();
  const { user } = useUser();

  const githubUsername = React.useMemo(() => {
    const acct = user?.externalAccounts?.find((a) =>
      a.provider?.toLowerCase().includes('github'),
    );
    return acct?.username ?? undefined;
  }, [user]);

  const raw = useQuery(
    api.dashboard.getOverviewMetrics,
    orgId && selectedProjectId
      ? {
          projectId: selectedProjectId,
          bindingId: selectedBindingId,
          orgId,
          githubUsername,
        }
      : 'skip',
  );

  if (!raw) {
    return {
      metrics: null,
      isLoading:
        projectLoading ||
        bindingLoading ||
        (!!orgId && !!selectedProjectId && raw === undefined),
    };
  }

  // Transform feed items from audit events
  const feed: DashboardFeedItem[] = raw.feed.map(
    (event: {
      action: string;
      description: string;
      resourceType: string;
      metadata: Record<string, unknown> | undefined;
      createdAt: number;
      actorEmail: string;
    }) => ({
      title: event.description || ACTION_LABEL[event.action] || event.action,
      meta: event.actorEmail || event.resourceType || '',
      ago: formatTimeAgo(event.createdAt),
      status: ACTION_STATUS[event.action] ?? 'info',
    }),
  );

  return {
    metrics: {
      enrichmentScore: raw.enrichmentScore,
      componentCoverage: raw.componentCoverage,
      tokenDiscovery: raw.tokenDiscovery,
      recentActivity: raw.recentActivity,
      connectedRepos: raw.connectedRepos,
      findings: raw.findings ?? {
        open: 0,
        byRule: {},
        trend: [],
        resolvedThisWeek: 0,
      },
      repoPosture: raw.repoPosture ??
        raw.findings ?? {
          open: 0,
          byRule: {},
          trend: [],
          resolvedThisWeek: 0,
        },
      prDrift: raw.prDrift ?? {
        open: 0,
        byRule: {},
        trend: raw.driftTrend ?? [],
        resolvedThisWeek: 0,
      },
      decisionIntelligence: raw.decisionIntelligence ?? {
        total: 0,
        token: 0,
        component: 0,
        safeFixes: 0,
        needsReview: 0,
        policyNeeded: 0,
        topKind: null,
        topTitle: null,
      },
      driftCaught: raw.driftCaught ?? {
        thisPeriod: 0,
        priorPeriod: 0,
        percentChange: 0,
      },
      driftTrend: raw.driftTrend ?? [],
      driftShipped: raw.driftShipped ?? {
        thisPeriod: 0,
        priorPeriod: 0,
        percentChange: 0,
        weeklyTrend: [],
      },
      myPrFindings: raw.myPrFindings ?? { open: 0, autofixable: 0 },
      areaRisk: raw.areaRisk ?? [],
      recommendedFixScenario: raw.recommendedFixScenario ?? null,
      scannerEvidence: raw.scannerEvidence ?? {
        available: false,
        repoFullName: null,
        branch: null,
        commitSha: null,
        scannedAt: null,
        components: [],
        tokens: [],
        diagnostics: [],
        componentAdoption: [],
        tokenAdoption: [],
      },
      feed,
    },
    isLoading: false,
  };
}
